import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, History, Plus, Trash2, X } from "lucide-react";
import api, { API_BASE } from "../../lib/api";
import StarterPackPanel from "./StarterPackPanel";
import { services } from "../../data/services";
import { subBrandFor } from "../../data/subBrands";

const TOOLKIT_TYPES = [
  "Methodology",
  "Artefact",
  "Deliverable",
  "Runbook",
  "Sector overlay",
];

const TYPE_HELP = {
  Methodology: "Engagement playbooks, kick-off checklists — how Ekselens runs the engagement.",
  Artefact: "Reusable named outputs (scorecards, policy templates, one-pagers).",
  Deliverable: "Document templates with {{placeholders}} — mail-merged with engagement data.",
  Runbook: "Step-by-step process recipes (e.g. how to run a Change Impact Workshop).",
  "Sector overlay": "Per-sector commentary the consultant splices into engine output.",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const downloadAuthed = async (path, filename) => {
  try {
    const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "toolkit";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
};

// ----- New-item dialog -----
const NewItemForm = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    service_slug: services[0]?.slug || "",
    type: "Methodology",
    title: "",
    description: "",
    changelog: "Initial version",
    file: null,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) {
      toast.error("Please choose a file");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    try {
      await api.post("/admin/toolkits", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Toolkit item created");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="toolkit-new-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="ek-surface max-w-2xl w-full p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="ek-eyebrow">Toolkit · Internal</div>
            <h3 className="ek-heading text-xl mt-2">Add a new toolkit item</h3>
          </div>
          <button type="button" onClick={onClose} className="ek-text-muted hover:text-black"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Service</span>
              <select
                value={form.service_slug}
                onChange={(e) => setForm({ ...form, service_slug: e.target.value })}
                className="mt-2 w-full border ek-line px-3 py-2 text-[14px] ek-surface"
                data-testid="toolkit-service-select"
              >
                {services.map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Type</span>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="mt-2 w-full border ek-line px-3 py-2 text-[14px] ek-surface"
                data-testid="toolkit-type-select"
              >
                {TOOLKIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {TYPE_HELP[form.type] ? (
                <p className="mt-1 text-[11.5px] ek-text-muted italic">{TYPE_HELP[form.type]}</p>
              ) : null}
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Title</span>
            <input
              type="text"
              required
              maxLength={200}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px]"
              data-testid="toolkit-title-input"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Description</span>
            <textarea
              required
              rows={3}
              maxLength={2000}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px]"
              data-testid="toolkit-description-input"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Initial changelog</span>
            <input
              type="text"
              maxLength={2000}
              value={form.changelog}
              onChange={(e) => setForm({ ...form, changelog: e.target.value })}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px]"
              data-testid="toolkit-changelog-input"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">File</span>
            <input
              type="file"
              required
              onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
              className="mt-2 w-full text-[14px]"
              data-testid="toolkit-file-input"
            />
          </label>
          <div className="flex gap-3 pt-3">
            <button type="submit" disabled={busy} className="ek-btn ek-btn-primary disabled:opacity-60" data-testid="toolkit-create-submit">
              {busy ? "Uploading…" : "Create item"}
            </button>
            <button type="button" onClick={onClose} className="ek-btn ek-btn-outline">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ----- New-version inline form (inside the expanded card) -----
const AddVersionForm = ({ itemId, onUploaded }) => {
  const [changelog, setChangelog] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file");
    if (!changelog.trim()) return toast.error("Please describe what changed");
    setBusy(true);
    const fd = new FormData();
    fd.append("changelog", changelog);
    fd.append("file", file);
    try {
      await api.post(`/admin/toolkits/${itemId}/versions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("New version uploaded");
      setChangelog("");
      setFile(null);
      e.target.reset();
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="ek-surface-2 border ek-line p-5 mt-5" data-testid={`toolkit-add-version-${itemId}`}>
      <h4 className="ek-heading text-sm">Upload new version</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <input
          type="text"
          placeholder="What changed?"
          value={changelog}
          onChange={(e) => setChangelog(e.target.value)}
          maxLength={2000}
          className="w-full border ek-line px-3 py-2 text-[14px]"
          data-testid={`toolkit-version-changelog-${itemId}`}
        />
        <input
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full text-[14px]"
          data-testid={`toolkit-version-file-${itemId}`}
        />
      </div>
      <button type="submit" disabled={busy} className="ek-btn ek-btn-primary mt-4 text-[13px] disabled:opacity-60" data-testid={`toolkit-version-submit-${itemId}`}>
        {busy ? "Uploading…" : "Upload new version"}
      </button>
    </form>
  );
};

// ----- Single toolkit card with expandable version history -----
const ToolkitCard = ({ item, onChanged }) => {
  const [open, setOpen] = useState(false);
  const service = services.find((s) => s.slug === item.service_slug);
  const subBrand = subBrandFor(item.service_slug);
  const isMarkdown = !item.current_filename || item.current_filename === "(markdown)" || item.current_content_type === "text/markdown";

  const remove = async () => {
    if (!window.confirm(`Delete "${item.title}" and all ${item.versions.length} versions? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/toolkits/${item.id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="ek-surface border ek-line" data-testid={`toolkit-card-${item.id}`}>
      <div className="p-5 lg:p-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap text-[10px] font-semibold tracking-[0.16em] uppercase">
            {subBrand ? (
              <span className="px-2 py-0.5 bg-[#7737F6] text-white">{subBrand.code}</span>
            ) : null}
            <span className="text-[#7737F6]">{item.type}</span>
            <span className="ek-text-muted">·</span>
            <span className="ek-text-muted">{service?.name || item.service_slug}</span>
            <span className="ek-text-muted">·</span>
            <span className="ek-text-soft">v{item.current_version}</span>
          </div>
          <h4 className="ek-heading text-lg ek-text mt-2 leading-tight">{item.title}</h4>
          <p className="ek-body text-[13.5px] mt-2 leading-relaxed">{item.description}</p>
          <div className="text-[11.5px] ek-text-muted mt-3">
            {isMarkdown ? (
              <span>One-pager · downloads as PDF / Word / Markdown · ~{Math.round(item.current_size_bytes / 1024) || 1} KB source · updated {fmt(item.updated_at)}</span>
            ) : (
              <span>{item.current_filename} · {Math.round(item.current_size_bytes / 1024)} KB · updated {fmt(item.updated_at)}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {isMarkdown ? (
            <>
              <button
                type="button"
                onClick={() => downloadAuthed(`/admin/toolkits/${item.id}/download?format=pdf`, `${item.title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')}.pdf`)}
                className="ek-btn ek-btn-primary text-[12px] px-4 py-2"
                data-testid={`toolkit-download-pdf-${item.id}`}
              >
                <Download size={13} /> Download PDF
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => downloadAuthed(`/admin/toolkits/${item.id}/download?format=docx`, `${item.title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')}.docx`)}
                  className="ek-btn ek-btn-outline text-[11px] px-3 py-1.5 flex-1"
                  data-testid={`toolkit-download-docx-${item.id}`}
                >
                  Word
                </button>
                <button
                  type="button"
                  onClick={() => downloadAuthed(`/admin/toolkits/${item.id}/download?format=md`, `${item.title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')}.md`)}
                  className="ek-btn ek-btn-outline text-[11px] px-3 py-1.5 flex-1"
                  data-testid={`toolkit-download-md-${item.id}`}
                >
                  .md
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => downloadAuthed(`/admin/toolkits/${item.id}/download`, item.current_filename)}
              className="ek-btn ek-btn-primary text-[12px] px-4 py-2"
              data-testid={`toolkit-download-${item.id}`}
            >
              <Download size={13} /> Download v{item.current_version}
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="text-[12px] ek-text-soft hover:text-black inline-flex items-center gap-1.5"
            data-testid={`toolkit-toggle-${item.id}`}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <History size={13} /> {item.versions.length} version{item.versions.length === 1 ? "" : "s"}
          </button>
          <button
            type="button"
            onClick={remove}
            className="text-[12px] text-red-600 hover:text-red-800 inline-flex items-center gap-1.5"
            data-testid={`toolkit-delete-${item.id}`}
          >
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>

      {open ? (
        <div className="border-t ek-line p-5 lg:p-6 bg-zinc-50/60" data-testid={`toolkit-history-${item.id}`}>
          <h4 className="ek-heading text-sm mb-3">Version history</h4>
          <table className="min-w-full text-[12.5px]">
            <thead className="ek-text-muted text-left">
              <tr>
                <th className="px-2 py-1">Version</th>
                <th className="px-2 py-1">Date</th>
                <th className="px-2 py-1">Changelog</th>
                <th className="px-2 py-1">File</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {[...item.versions].reverse().map((v) => (
                <tr key={v.version} className="border-t ek-line">
                  <td className="px-2 py-2 font-semibold text-[#7737F6]">v{v.version}{v.version === item.current_version ? " (current)" : ""}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{fmt(v.created_at)}</td>
                  <td className="px-2 py-2">{v.changelog}</td>
                  <td className="px-2 py-2 ek-text-soft">{v.filename} ({Math.round(v.size_bytes / 1024)} KB)</td>
                  <td className="px-2 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => downloadAuthed(`/admin/toolkits/${item.id}/versions/${v.version}/download`, v.filename)}
                      className="text-[12px] text-[#7737F6] hover:underline"
                      data-testid={`toolkit-version-download-${item.id}-${v.version}`}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <AddVersionForm itemId={item.id} onUploaded={onChanged} />
        </div>
      ) : null}
    </div>
  );
};

// ----- Main panel -----
const ToolkitsPanel = () => {
  const [items, setItems] = useState([]);
  const [serviceFilter, setServiceFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const params = {};
      if (serviceFilter) params.service_slug = serviceFilter;
      if (typeFilter) params.type = typeFilter;
      const { data } = await api.get("/admin/toolkits", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [serviceFilter, typeFilter]);

  useEffect(() => { reload(); }, [reload]);

  // Group by service so the user can see service coverage at a glance
  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (!map.has(it.service_slug)) map.set(it.service_slug, []);
      map.get(it.service_slug).push(it);
    }
    return [...map.entries()];
  }, [items]);

  return (
    <div data-testid="admin-toolkits-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Internal toolkits</h2>
          <p className="ek-text-muted text-[13px] mt-1">Versioned IP for Ekselens consultants. Not visible to the public.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="ek-btn ek-btn-primary text-[13px]"
          data-testid="toolkit-new-btn"
        >
          <Plus size={14} /> New toolkit item
        </button>
      </div>

      <div className="bg-[#F4F2FB] border border-[#E1D7FA] px-4 py-3 mb-6 text-[12.5px] ek-text-soft leading-relaxed">
        <strong className="text-[#7737F6]">Catalogue updated.</strong>{" "}
        <em>Maturity</em>, <em>Discovery</em> and <em>Workshop</em> templates are now produced live by the EKSELENS360 Maturity Engine — use the <strong>Assessments</strong> tab to run a diagnostic and download branded PDF / PPTX / DOCX / XLSX outputs. The toolkit library now holds <strong>Methodology</strong>, <strong>Artefact</strong>, <strong>Deliverable</strong>, <strong>Runbook</strong> and <strong>Sector overlay</strong> templates only.
      </div>

      <StarterPackPanel onSeeded={reload} />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={serviceFilter}
          onChange={(e) => setServiceFilter(e.target.value)}
          className="border ek-line px-3 py-2 text-[13px] ek-surface"
          data-testid="toolkit-filter-service"
        >
          <option value="">All services</option>
          {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border ek-line px-3 py-2 text-[13px] ek-surface"
          data-testid="toolkit-filter-type"
        >
          <option value="">All types</option>
          {TOOLKIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="ek-text-muted text-[13px]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="ek-surface border ek-line p-10 text-center" data-testid="toolkits-empty">
          <h3 className="ek-heading text-lg ek-text">No toolkit items yet.</h3>
          <p className="ek-body text-[14px] mt-3 max-w-md mx-auto">
            Build your IP library service-by-service. Use the seven types to keep coverage balanced — every service should eventually have a methodology, discovery questionnaire, maturity model and at least one workshop pack.
          </p>
          <button type="button" onClick={() => setShowNew(true)} className="ek-btn ek-btn-primary mt-6 text-[13px]" data-testid="toolkit-new-empty-btn">
            <Plus size={14} /> Upload the first item
          </button>
        </div>
      ) : (
        <div className="space-y-10">
          {grouped.map(([slug, list]) => {
            const service = services.find((s) => s.slug === slug);
            const sb = subBrandFor(slug);
            return (
              <section key={slug} data-testid={`toolkit-group-${slug}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="ek-heading text-base ek-text">
                    {sb ? <span className="text-[#7737F6] mr-2">{sb.code}</span> : null}
                    {service?.name || slug}
                  </h3>
                  <span className="text-[11px] ek-text-muted tracking-wide uppercase">{list.length} item{list.length === 1 ? "" : "s"}</span>
                </div>
                <div className="space-y-3">
                  {list.map((it) => <ToolkitCard key={it.id} item={it} onChanged={reload} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {showNew ? <NewItemForm onClose={() => setShowNew(false)} onCreated={reload} /> : null}
    </div>
  );
};

export default ToolkitsPanel;
